import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Collapse, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AvatarStyled,
  MessageCard,
  MessageItem,
  MessageItemContainer,
  MessageItemInnerContainer,
  MessageOrganizer,
  MyMessageItem,
  MyMessageItemInnerContainer,
  SeparatorBorder,
} from "./requests-messages-card.style";
import {
  MyHrDataRequestSubCollapseHeader,
  MyHrNewMessage,
} from "my-hr/components";
import { MyLeaveNewMessage } from "my-leave/components";
import { MyTimeNewMessage } from "my-time/components";
import { MyTimesheetNewMessage } from "my-timesheet/components";
import { OcAvatar } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";

interface Message {
  _id: string;
  createdAt: string;
  createdBy: string;
  employeeName?: string;
  message: string;
}

interface OcRequestsMessagesCardProps {
  messages: Message[];
  data: any;
  module: string;
  loading: boolean;
  typeGroup?: string;
  type?: string;
  selectedId?: string;
  typeGroupId?: string;
  disabled: boolean;
  refetch?: () => void;
  isMobile?: boolean;
  role: string;
}

export const OcRequestsMessagesCard: React.FC<OcRequestsMessagesCardProps> = ({
  messages,
  data,
  module,
  loading,
  typeGroup,
  type,
  selectedId,
  typeGroupId,
  disabled,
  refetch,
  isMobile,
  role,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { employeeCode } = useOcCurrentUserState();

  const [messageExpanded, setMessageExpanded] = useState(module !== "MyHr");

  const handleMessageExpandClick = () => {
    setMessageExpanded(!messageExpanded);
  };

  let locale = i18n.language.substr(0, 2).toLowerCase();

  return (
    <Box>
      <MyHrDataRequestSubCollapseHeader
        expanded={messageExpanded}
        handleExpandClick={handleMessageExpandClick}
        label={t("OC_MESSAGES")}
        badge={messages.length + " " + t("MY_HR_MESSAGE")}
        isMobile={isMobile ?? false}
        type="message"
        data={[]}
      />
      <Collapse in={messageExpanded} timeout="auto">
        <MessageCard>
          <SeparatorBorder />
          <Box height="24px" />
          {!loading &&
            messages?.map((messageItem, index) => {
              let createdAt = moment(messageItem?.createdAt)
                .locale(locale)
                .format("MMM DD. HH:mm");

              return (
                <Box key={`${index}_${messageItem?._id}`}>
                  {messageItem?.createdBy === employeeCode ? (
                    <MessageItemContainer>
                      <MessageOrganizer>
                        <MyMessageItemInnerContainer>
                          <Typography
                            variant="caption"
                            color={theme.palette.gray60.main}
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {createdAt}
                          </Typography>
                        </MyMessageItemInnerContainer>
                        <MyMessageItem>{messageItem?.message}</MyMessageItem>
                      </MessageOrganizer>
                    </MessageItemContainer>
                  ) : (
                    <MessageItemContainer>
                      <AvatarStyled>
                        <OcAvatar employee={messageItem?.createdBy} />
                      </AvatarStyled>
                      <MessageOrganizer>
                        <MessageItemInnerContainer>
                          <Typography
                            variant="caption"
                            color={theme.palette.gray60.main}
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {messageItem?.employeeName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={theme.palette.gray60.main}
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {createdAt}
                          </Typography>
                        </MessageItemInnerContainer>
                        <MessageItem>{messageItem?.message}</MessageItem>
                      </MessageOrganizer>
                    </MessageItemContainer>
                  )}
                </Box>
              );
            })}
          <Box height="8px" />
          {module === "MyHr" && (
            <MyHrNewMessage
              loading={loading}
              selectedRequest={selectedId ?? ""}
              typeGroupId={typeGroupId}
              typeGroup={typeGroup || ""}
              type={type ?? ""}
              disabled={false}
            />
          )}
          {module === "MyLeave" && (
            <MyLeaveNewMessage
              loading={loading}
              selectedRequest={data?._id}
              selectedEmployeeId={data?.createdBy}
              disabled={disabled}
              role={role}
            />
          )}
          {module === "MyTime" && (
            <MyTimeNewMessage loading={loading} selectedRequest={data?._id} />
          )}
          {module === "MyTimesheet" && (
            <MyTimesheetNewMessage
              loading={loading}
              selectedRequest={data?._id}
              refetch={refetch ?? (() => {})}
            />
          )}
        </MessageCard>
      </Collapse>
    </Box>
  );
};

export default OcRequestsMessagesCard;
